import Slider, { SliderProps } from '@mui/material/Slider';
import { UnitBase } from 'hooks/useDisplayUnits/useDisplayUnits';
import { CustomUnitFields, UnitSystem } from 'providers/api';
import { clampToRange, round } from 'utils';
import { isCustomUnitField } from 'utils/unitConversions';

const STEP_DECIMAL_PLACES = 4;

interface UnitSystemNumberSliderProps extends SliderProps {
  unitSystem: UnitSystem;
  unitInfo: UnitBase;
  min: number;
  max: number;
  decimalPlaces: number;
  customUnitFields: CustomUnitFields[];
  // Step in the stored (metric) unit, shown in the displayed unit. Overrides `step`.
  baseStep?: number;
  onSliderChange: (event: Event, newValue: number | number[]) => void
}

// MUI treats a step of 0 as "snap to marks" and, with no marks, emits NaN; fall back to its default step instead.
const toDisplayStep = (baseStep: number, toDisplay: (value: number) => number) => {
  const step = round(toDisplay(baseStep), STEP_DECIMAL_PLACES);
  return step > 0 ? step : undefined;
};

const UnitSystemNumberSlider = (props: UnitSystemNumberSliderProps) => {
  const { value, unitSystem, customUnitFields, unitInfo, min, max, decimalPlaces, baseStep, onSliderChange, ...other } = props;
  const checkedValue = value ?? min;
  const isCustom = isCustomUnitField(unitSystem, customUnitFields, unitInfo.customUnitField);
  const toDisplay = (baseValue: number) => (isCustom ? unitInfo.imperial.conversion(baseValue) : baseValue);
  const stepProps = baseStep === undefined ? {} : { step: toDisplayStep(baseStep, toDisplay) };

  const handleOnChange = (event: Event, newValue: number | number[]) => {
    const currentValue = Array.isArray(newValue) ? newValue[0] : newValue;
    if (!Number.isFinite(currentValue)) return;
    const unitSystemBaseValue = isCustom ? unitInfo.metric.conversion(currentValue) : currentValue;

    // The displayed bounds are rounded, so converting back can land just outside the real range.
    onSliderChange(event, clampToRange(unitSystemBaseValue, min, max));
  };

  return (
    <Slider
      color="secondary"
      {...other}
      {...stepProps}
      value={isCustom ? round(unitInfo.imperial.conversion(checkedValue as number), decimalPlaces) : checkedValue}
      onChange={handleOnChange}
      valueLabelDisplay="auto"
      min={isCustom ? round(unitInfo.imperial.conversion(min), 0) : min}
      max={isCustom ? round(unitInfo.imperial.conversion(max), 0) : max}
    />
  );
};

export default UnitSystemNumberSlider;
